import React, { useState } from "react";
import { Download, FileText } from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../services/api";
import { getCurrentFinancialYear } from "./tax-planning/taxPlanning";
import { downloadForm16Pdf } from "../../utils/form16PdfExport";

function fyOptions() {
  const current = getCurrentFinancialYear();
  const startYear = Number(String(current).slice(0, 4)) - 2;
  const out = [];
  for (let i = 0; i < 5; i += 1) {
    const y = startYear + i;
    const end = String((y + 1) % 100).padStart(2, "0");
    out.push(`${y}-${end}`);
  }
  return out.reverse();
}

export default function Form16({ employees = [], darkMode = false }) {
  const [selectedYear, setSelectedYear] = useState(getCurrentFinancialYear());
  const [busyId, setBusyId] = useState(null);

  const fetchForm16Data = async (employee) => {
    const res = await payrollOpsService.getForm16(employee.id, selectedYear);
    return res?.data || res;
  };

  const handleDownloadForm16Pdf = async (employee) => {
    if (!selectedYear) {
      toast.error("Select a financial year");
      return;
    }
    setBusyId(employee.id);
    try {
      const data = await fetchForm16Data(employee);
      if (!data?.partA?.monthlyBreakdown?.length && !data?.partA?.totalTdsDeducted) {
        toast.error("No TDS records for this employee in the selected FY");
        return;
      }
      downloadForm16Pdf(data);
      toast.success("Form 16 PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Unable to generate Form 16 PDF");
    } finally {
      setBusyId(null);
    }
  };

  const handleDownloadForm16Json = async (employee) => {
    if (!selectedYear) {
      toast.error("Select a financial year");
      return;
    }
    setBusyId(`json-${employee.id}`);
    try {
      const data = await fetchForm16Data(employee);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Form16_${employee.empCode || employee.id}_${selectedYear}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Form 16 JSON downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Unable to generate Form 16");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={`rounded-lg p-4 shadow-sm ${darkMode ? "bg-[#1a1a1a]" : "bg-white"}`}>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">Financial Year</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className={`rounded-lg border px-3 py-1.5 text-sm ${
              darkMode ? "border-gray-700 bg-[#2a2a2a] text-white" : "border-gray-200 bg-white"
            }`}
          >
            {fyOptions().map((fy) => (
              <option key={fy} value={fy}>
                {fy}
              </option>
            ))}
          </select>
        </div>
        <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
          Part A (TDS deducted) + Part B (salary & tax computation) from payroll TDS records.
        </p>
      </div>

      <table className="mt-4 min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
              Employee
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
              Code
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 ${darkMode ? "bg-[#1a1a1a]" : "bg-white"}`}>
          {employees.map((employee) => (
            <tr key={employee.id}>
              <td className="whitespace-nowrap px-6 py-4">{employee.name}</td>
              <td className="whitespace-nowrap px-6 py-4">{employee.empCode || employee.id}</td>
              <td className="whitespace-nowrap px-6 py-4">
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => handleDownloadForm16Pdf(employee)}
                    disabled={!selectedYear || busyId === employee.id}
                    className="flex items-center text-redhalo-navy hover:text-redhalo-red disabled:opacity-50"
                  >
                    <Download size={18} className="mr-1" />
                    {busyId === employee.id ? "Generating…" : "PDF"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDownloadForm16Json(employee)}
                    disabled={!selectedYear || busyId === `json-${employee.id}`}
                    className="flex items-center text-gray-600 hover:text-gray-900 disabled:opacity-50"
                  >
                    <FileText size={16} className="mr-1" />
                    JSON
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {employees.length === 0 && (
        <p className="py-8 text-center text-sm text-gray-500">No employees in scope. Apply filters to load employees.</p>
      )}
    </div>
  );
}
